import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Header from '../Header';
import { COOKIE_BANNER_ID } from '../CookieBanner';
import LocationSearchContainer, { MAXIMUM_SAVED_ADDRESSES } from '../LocationSearchContainer';
import HomepageHeroCuisineImage from '../../components/HomepageHeroCuisineImage';
import { getHasSuggestions } from '../../selectors/homepageHeroSelectors';
import scrollTo from '../../utils/scrollTo';
import i18n from '../../utils/i18n';
import { isCondensedMobileLayoutActive } from '../../utils/pages/homepage';
import { trackLocationSearchSeen } from '../../actions/tracking/homepage';

class HomepageHero extends Component {
  static propTypes = {
    tld: PropTypes.string.isRequired,
    isAppBannerShown: PropTypes.bool,
    isAppBannerCollapsed: PropTypes.bool,
    cuisineImage: PropTypes.string.isRequired,
    heroColor: PropTypes.string.isRequired,
    savedAddressCount: PropTypes.number.isRequired,
    trackLocationSearchSeenPercentage: PropTypes.func.isRequired,
    showCondensedMobileLayout: PropTypes.bool,
    hasSuggestions: PropTypes.bool,
  };

  static defaultProps = {
    isAppBannerShown: false,
    isAppBannerCollapsed: false,
    showCondensedMobileLayout: false,
    hasSuggestions: false,
  };

  focusSearch = () => scrollTo(this.headerEl);

  headerRef = (el) => {
    this.headerEl = el;
  };

  decimalToPercentage = (decimal) => decimal * 100;

  handleIntersect = (observers) => {
    // the user has scrolled & caused the intersection to change; reset the timer
    if (this.trackingTimeout) {
      clearTimeout(this.trackingTimeout);
    }

    // `observers` is an array of all IntersectionObservers on the page. if we add any other
    // IntersectionObservers, we'll need to iterate through the array to check which is which.
    this.currentRatio = this.decimalToPercentage(observers[0].intersectionRatio);

    // the intersection changes every time the user scrolls, if the LocationSearch is at least
    // partially visible. setting a timeout means we can track when the user:
    //   1. sees the LocationSearch and is not scrolling
    //   2. scrolls past LocationSearch, slowly enough that we can say they've "seen" it (3 sec)
    this.trackingTimeout = setTimeout(() => {
      const shouldUpdateMax = this.currentRatio > this.maxRatio;

      if (shouldUpdateMax) {
        this.maxRatio = this.currentRatio;
        this.props.trackLocationSearchSeenPercentage(this.maxRatio, true);
      }
    }, 3000);
  };

  componentDidMount() {
    this.currentRatio = 0.0;
    this.maxRatio = 0.0;

    // allow for cookie banner overlay at the bottom of the page
    const cookieBanner = document.getElementById(COOKIE_BANNER_ID);
    const rootMargin = cookieBanner ? `0px 0px -${cookieBanner.offsetHeight}px 0px` : '0px';

    const threshold = [
      0.0,
      0.05,
      0.1,
      0.15,
      0.2,
      0.25,
      0.3,
      0.35,
      0.4,
      0.45,
      0.5,
      0.55,
      0.6,
      0.65,
      0.7,
      0.75,
      0.8,
      0.85,
      0.9,
      0.95,
      1.0,
    ];

    const options = {
      root: null,
      rootMargin,
      threshold,
    };

    try {
      this.observer = new IntersectionObserver(this.handleIntersect, options);
      this.observer.observe(this.locationSearchElement);
    } catch (err) {
      // IntersectionObserver not available; do nothing
    }
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.unobserve(this.locationSearchElement);
    }

    if (this.trackingTimeout) {
      clearTimeout(this.trackingTimeout);
    }
  }

  render() {
    const {
      isAppBannerShown,
      isAppBannerCollapsed,
      cuisineImage,
      heroColor,
      savedAddressCount,
      showCondensedMobileLayout,
      hasSuggestions,
    } = this.props;

    const locationSearch = (
      <LocationSearchContainer
        onFocusSearch={this.focusSearch}
        trackingPage="Homepage"
        isModal={false}
        showAlternativeCaption={showCondensedMobileLayout}
        containerRef={(el) => {
          this.locationSearchElement = el;
        }}
      />
    );

    const sharedHeroProps = {
      headerRef: this.headerRef,
      isAppBannerShown,
      isAppBannerCollapsed,
    };

    const headingText = showCondensedMobileLayout
      ? i18n.gettext('landingPage.home.foodFreedom.heroHeadingCondensed')
      : i18n.gettext('landingPage.home.foodFreedom.heroHeading');

    return (
      <HomepageHeroCuisineImage
        headerComponent={<Header isTransparent isHomepage logoColor="white" />}
        headingText={headingText}
        cuisineImage={cuisineImage}
        heroShapeColor={heroColor}
        savedAddressCount={savedAddressCount}
        showCondensedMobileLayout={showCondensedMobileLayout}
        overlapContent={!hasSuggestions}
        {...sharedHeroProps}
      >
        {locationSearch}
      </HomepageHeroCuisineImage>
    );
  }
}

const mapStateToProps = (state) => {
  const { request, homepageFeaturedContent } = state;
  const { locations: userAddresses } = state.location.recentAddresses;

  return {
    tld: request.country.tld,
    cuisineImage: homepageFeaturedContent.heroCuisineImage,
    heroColor: homepageFeaturedContent.heroShapeColor,
    savedAddressCount: userAddresses.slice(0, MAXIMUM_SAVED_ADDRESSES).length,
    hasSuggestions: getHasSuggestions(state),
    userAddresses,
    showCondensedMobileLayout: isCondensedMobileLayoutActive(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  trackLocationSearchSeenPercentage: (percentage) => dispatch(trackLocationSearchSeen(percentage)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(HomepageHero);
